import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authAPI, usersAPI, rolesAPI, permissionsAPI, getCurrentUser, setCurrentUser } from '../services/api';
import { oauthAPI } from '../services/api';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentUser, setCurrentUserState] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(false);

  useEffect(() => {
    const loadUser = async () => {
      const storedOAuthUser = localStorage.getItem('oauth_user');
      const storedOAuthToken = localStorage.getItem('oauth_token');

      if (storedOAuthUser && storedOAuthToken) {
        try {
          const oauthUser = JSON.parse(storedOAuthUser);
          if (oauthUser.roleId && oauthUser.permissions) {
            const userData = {
              id: oauthUser.id,
              username: oauthUser.username,
              roleId: oauthUser.roleId,
              role: oauthUser.role,
              permissions: oauthUser.permissions
            };
            setCurrentUserState(userData);
            setCurrentUser(userData);
          }
        } catch (err) {
          console.error('Error loading OAuth user:', err);
        }
      }

      setIsInitialized(true);
    };

    loadUser();
  }, []);

  const currentUserRef = useRef(currentUser);
  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    const checkOAuthUser = () => {
      try {
        const storedOAuthUser = localStorage.getItem('oauth_user');
        if (storedOAuthUser) {
          const oauthUser = JSON.parse(storedOAuthUser);
          if (oauthUser.roleId && oauthUser.permissions) {
            const userData = {
              id: oauthUser.id,
              username: oauthUser.username,
              roleId: oauthUser.roleId,
              role: oauthUser.role,
              permissions: oauthUser.permissions
            };
            if (currentUserRef.current?.id !== userData.id ||
                currentUserRef.current?.roleId !== userData.roleId) {
              setCurrentUserState(userData);
              setCurrentUser(userData);
            }
          } else if (currentUserRef.current) {
            setCurrentUserState(null);
            setCurrentUser(null);
          }
        }
      } catch (err) {
        console.error('Error checking OAuth user:', err);
      }
    };

    checkOAuthUser();

    const handleStorageChange = (e) => {
      if (e.key === 'oauth_user') {
        setTimeout(checkOAuthUser, 0);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    const handleOAuthUpdate = () => {
      setTimeout(checkOAuthUser, 0);
    };
    window.addEventListener('oauthUserUpdated', handleOAuthUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('oauthUserUpdated', handleOAuthUpdate);
    };
  }, []);

  const hasAttemptedLoadRef = useRef(false);
  const currentUserIdRef = useRef(null);

  useEffect(() => {
    if (currentUserIdRef.current !== currentUser?.id) {
      hasAttemptedLoadRef.current = false;
      currentUserIdRef.current = currentUser?.id || null;
    }

    if (hasAttemptedLoadRef.current) return;

    if (currentUser && currentUser.permissions && Array.isArray(currentUser.permissions)) {
      const hasAdminPermissions = currentUser.permissions.some(perm =>
        perm.includes('admin.') ||
        perm.includes('users.') ||
        perm.includes('roles.') ||
        currentUser.roleId === 'admin' ||
        currentUser.roleId === 'administrator'
      );

      if (hasAdminPermissions) {
        hasAttemptedLoadRef.current = true;
        loadUsers();
        loadRoles();
        loadPermissions();
      }
    }
  }, [currentUser]);

  const loadUsers = async () => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const response = await usersAPI.getAll();
      if (response.success) {
        setUsers(response.data);
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        hasAttemptedLoadRef.current = true;
        return;
      }
      if (!err.response || err.response.status !== 403) {
        console.error('Error loading users:', err);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadRoles = async () => {
    if (loadingRoles) return;

    setLoadingRoles(true);
    try {
      const response = await rolesAPI.getAll();
      if (response.success) {
        setRoles(response.data);
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        hasAttemptedLoadRef.current = true;
        return;
      }
      if (!err.response || err.response.status !== 403) {
        console.error('Error loading roles:', err);
      }
    } finally {
      setLoadingRoles(false);
    }
  };

  const loadPermissions = async () => {
    if (loadingPermissions) return;

    setLoadingPermissions(true);
    try {
      const response = await permissionsAPI.getAll();
      if (response.success) {
        const permissionsObj = {};
        response.data.forEach(perm => {
          permissionsObj[perm.id] = {
            name: perm.name,
            category: perm.category
          };
        });
        setPermissions(permissionsObj);
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        hasAttemptedLoadRef.current = true;
        return;
      }
      if (!err.response || err.response.status !== 403) {
        console.error('Error loading permissions:', err);
      }
    } finally {
      setLoadingPermissions(false);
    }
  };

  const login = async (usernameOrEmail, password) => {
    setError(null);
    try {
      const response = await authAPI.login(usernameOrEmail, password);
      if (response.success) {
        setCurrentUserState(response.user);
        if (response.user.roleId === 'admin') {
          await Promise.all([loadUsers(), loadRoles(), loadPermissions()]);
        }
        return true;
      } else {
        setError(response.message || 'Login failed');
        return false;
      }
    } catch (err) {
      setError(err.message || 'Login failed');
      return false;
    }
  };

  const logout = async () => {
    try {
      setCurrentUserState(null);
      setCurrentUser(null);
      setUsers([]);
      setRoles([]);
      setPermissions({});
      localStorage.removeItem('current_user');
      localStorage.removeItem('auth_token');
      hasAttemptedLoadRef.current = false;
      currentUserIdRef.current = null;
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  useEffect(() => {
    const verifyUser = async () => {
      const storedUser = getCurrentUser();
      const storedToken = localStorage.getItem('auth_token') || localStorage.getItem('oauth_token');

      if (storedUser && storedToken) {
        try {
          const response = await authAPI.getCurrentUser();
          if (response.success) {
            setCurrentUserState(response.user);
            if (response.user.roleId === 'admin') {
              await Promise.all([loadUsers(), loadRoles(), loadPermissions()]);
            }
          } else {
            setCurrentUserState(null);
            setCurrentUser(null);
          }
        } catch (err) {
          setCurrentUserState(null);
          setCurrentUser(null);
        }
      }
    };

    if (isInitialized) {
      verifyUser();
    }
  }, [isInitialized]);

  const addUser = async (userData) => {
    try {
      const response = await usersAPI.create(userData);
      if (response.success) {
        await loadUsers();
        return response.data;
      }
      throw new Error(response.message || 'Failed to create user');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateUser = async (id, updates) => {
    try {
      const response = await usersAPI.update(id, updates);
      if (response.success) {
        await loadUsers();
        if (currentUser && currentUser.id === parseInt(id)) {
          const updatedUser = { ...currentUser, ...updates };
          setCurrentUserState(updatedUser);
          setCurrentUser(updatedUser);
        }
        return response.data;
      }
      throw new Error(response.message || 'Failed to update user');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteUser = async (id) => {
    try {
      const response = await usersAPI.delete(id);
      if (response.success) {
        await loadUsers();
        return true;
      }
      throw new Error(response.message || 'Failed to delete user');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const addRole = async (roleData) => {
    try {
      const response = await rolesAPI.create(roleData);
      if (response.success) {
        await loadRoles();
        return response.data;
      }
      throw new Error(response.message || 'Failed to create role');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateRole = async (id, updates) => {
    try {
      const response = await rolesAPI.update(id, updates);
      if (response.success) {
        await loadRoles();
        return response.data;
      }
      throw new Error(response.message || 'Failed to update role');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteRole = async (id) => {
    try {
      const response = await rolesAPI.delete(id);
      if (response.success) {
        await loadRoles();
        return true;
      }
      throw new Error(response.message || 'Failed to delete role');
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const hasPermission = (permission) => {
    if (currentUser) {
      if (currentUser.roleId === 'admin') return true;
      return currentUser.permissions && currentUser.permissions.includes(permission);
    }

    try {
      const storedOAuthUser = localStorage.getItem('oauth_user');
      if (storedOAuthUser) {
        const oauthUser = JSON.parse(storedOAuthUser);
        if (oauthUser.adminUser) {
          if (oauthUser.adminUser.roleId === 'admin') return true;
          return oauthUser.adminUser.permissions && oauthUser.adminUser.permissions.includes(permission);
        }
      }
    } catch (err) {
    }

    return false;
  };

  const hasAnyPermission = (permissionList) => {
    return permissionList.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissionList) => {
    return permissionList.every(permission => hasPermission(permission));
  };

  const getUserRole = (userId) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      return roles.find(r => r.id === user.roleId);
    }
    return null;
  };

  const value = {
    users,
    roles,
    permissions,
    currentUser,
    isInitialized,
    loading,
    error,
    addUser,
    updateUser,
    deleteUser,
    addRole,
    updateRole,
    deleteRole,
    login,
    logout,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getUserRole,
    refreshUsers: loadUsers,
    refreshRoles: loadRoles,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    return {
      users: [],
      roles: [],
      permissions: {},
      currentUser: null,
      isInitialized: false,
      loading: false,
      error: null,
      addUser: () => {},
      updateUser: () => {},
      deleteUser: () => {},
      addRole: () => {},
      updateRole: () => {},
      deleteRole: () => {},
      login: () => Promise.resolve(false),
      logout: () => {},
      hasPermission: () => false,
      hasAnyPermission: () => false,
      hasAllPermissions: () => false,
      getUserRole: () => null,
      refreshUsers: () => {},
      refreshRoles: () => {},
    };
  }
  return context;
};
